// v15.1
// noinspection JSUnresolvedFunction,JSUnresolvedVariable

import * as THREE from 'three';
import {Display} from "./Display";
import {Load} from "./Load";
import {Podcastplayer} from "./Podcastplayer";
import {Basic} from "./Basic";

export class Sculpture
{
    constructor(myCanvas)
    {
        // general settings
        this.navHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--nav-height'));
        this.color = getComputedStyle(document.documentElement).getPropertyValue('--color');
        this.counter = 0;
        this.volume = 1;
        this.activeAudioNo = 0;
        this.audios = {};
        this.controls = '';
        this.controlButton = '';
        this.playButton = '';
        this.trackLabel = '';
        this.trackLink = '';
        this.zoomLevel = 1;
        this.debug = false;
        this.doCalculations = true;

        // performance testing
        this.perfTest = true;
        this.perfThreshold = 66; // = 15 FPS
        this.perfSlow = 0;

        // variables for scroll processing
        this.lastScrollY = 0;
        this.scrollSceneX = undefined;
        this.scrollCameraY = undefined;

        // basic confiuration
        this.reset_config();

        // settings for different devices / settings
        if(Display.device === 'smartphone')
        {
            this.set_mobile();
        }
        else if(Display.orientation === 'portrait')
        {
            this.set_portrait();
        }
        else
        {
            this.set_desktop();
        }

        // pole objects
        this.poles = [];
        this.moveDirections = [];
        this.scaleDirections = [];
        this.objectScale = [];

        // set move and scale direction
        for(let i=0; i<this.ringNumber; i++)
        {
            this.moveDirections[i] = 'up';
            this.scaleDirections[i] = 'up';
            this.objectScale[i] = 1;
        }

        // canvas
        this.canvas = myCanvas;

        // renderer
        this.renderer = new THREE.WebGLRenderer({
            alpha: true,
            antialias: true,
            setPixelRatio: Display.dpi,
            canvas: this.canvas
        });
        this.renderer.setSize(Display.x, Display.y);

        // camera
        this.camera = new THREE.OrthographicCamera();
        this.camera.near = 0;
        this.camera.far = this.frustumSize * 5;
        this.set_cameraViewport();
        this.camera.position.set(0,0, this.frustumSize);
        this.camera.lookAt(0,0,0);

        // scene
        this.scene = new THREE.Scene();

        // dummies
        this.dummy = new THREE.Object3D();
        this.scale = new THREE.Vector3();
        this.position = new THREE.Vector3();

        // audio
        this.audioRatio = 255 / this.poleLengh;

        // load tracks
        const source = document.getElementById('w--sound-source');
        source
            ? this.prepare_player(
                    JSON.parse(source.querySelector('.w__sound--source').textContent),
                    source.querySelector('.w__sound--controls').innerHTML)
            : this.get_audioTracks();
    }

    get_audioTracks()
    {
        Load.post({
            "f": "audiosculpture"
        }).then(r => {
            this.prepare_player(r['audios'], r['html']);
        }).catch(error => {
            console.log(error);
        });
    }

    prepare_player(audios, html)
    {
        this.audios = audios;
        this.controlButton = document.createElement('button');
        this.controlButton.className = 'w__button w__sculpture--controls-toggle w__js-button';
        this.controlButton.dataset.f = 'sculpture';
        this.controlButton.dataset.action = 'toggle-controls';
        this.controlButton.title = (Basic.lang === 'de') ? 'Player-Steuerung ein/aus' : "Player controls on/off";
        document.getElementById('w--nav').appendChild(this.controlButton);

        // show button if no header
        if(!document.body.querySelector('.w__header--full,.w__header--half'))
        {
            this.controlButton.classList.add('active');
        }

        this.controls = document.createElement('div');
        this.controls.classList.add('w__sculpture--controls');
        this.controls.innerHTML = html;
        document.getElementById('w--nav').appendChild(this.controls);

        this.trackLabel = this.controls.querySelector('.w__sculpture--track');
        this.trackLink = document.createElement('a');

        this.trackLabel.appendChild(this.trackLink);
        this.trackLink.classList.add('w__il');
        this.trackLink.classList.add('w__hover--color');

        this.set_audioTrack();
    }

    set_desktop()
    {
        // positions and scales
        if(Display.deviceSize === 'small')
        {
            this.definitions = [
                [0,     0,      -15, 1],
                [35,    20,     -15, 1],
                [-35,   -10,    -15, 1],
                [13,    12,     -15, 1],
                [-13.5, -17,    -15, 1],
                [-8,    8,      -15, 1]
            ];
            this.ringsPerPoleNumber = 24;
            this.fftSize = 4096;
        }
        else
        {
            this.definitions = [
                [0,     0,      -15, 1],
                [35,    20,     -15, 1],
                [-35,   -10,    -15, 1],
                [13,    12,     -15, 1],
                [-13.5, -17,    -15, 1],
                [-8,    8,      -15, 1],
                [25,    -20,    -15, 1],
                [-25,   17,     -15, 1]
            ];
            this.ringsPerPoleNumber = 32;
            this.fftSize = 8192;
        }

        // ring size
        this.radiusSize = 5;
        this.tubeSize = .04;

        // number of poles and rings ring echoes
        this.frustumSize = 100;
        this.poleNumber = this.definitions.length;
        this.ringNumber = this.poleNumber * this.ringsPerPoleNumber;
        this.poleLengh = this.frustumSize / 3; // percent of frustum
    }

    set_portrait()
    {
        // positions and scales
        this.definitions = [
            [0, 0, 5, 1],
            [28, 40, 15, 1],
            [-30.5, -10, -20, 1],
            [21, 12, -12, 1],
            [-10.5, -22, -35, 1],
            [-18, 8, 12.5, 1]
        ];

        // ring size
        this.radiusSize = 7.5;
        this.tubeSize = .05;

        // number of poles and rings ring echoes
        this.frustumSize = 75;
        this.poleNumber = this.definitions.length;
        this.ringsPerPoleNumber = 32;
        this.ringNumber = this.poleNumber * this.ringsPerPoleNumber;
        this.poleLengh = this.frustumSize / 2; // percent of frustum
        this.fftSize = 4096;
    }

    set_mobile()
    {
        // positions and scales
        this.definitions = [
            [0, 0, 5, 1],
            [19, 7, -12, 1],
            [-6.5, -12, -35, 1],
            [-16, 3, 12.5, 1]
        ];

        // size
        this.radiusSize = 10;
        this.tubeSize = .075;

        // number of poles and rings ring echoes
        this.frustumSize = 50;
        this.poleNumber = this.definitions.length;
        this.ringsPerPoleNumber = 16;
        this.ringNumber = this.poleNumber * this.ringsPerPoleNumber;
        this.poleLengh = this.frustumSize / 2; // percent of frustum
        this.fftSize = 2048;
    }

    add_soundOnButton(myHeader)
    {
        this.playButton = document.createElement('button');
        this.playButton.classList.add('w__button');
        this.playButton.classList.add('w__sculpture--play');
        this.playButton.classList.add('w__js-button');
        this.playButton.title = 'sound on';
        this.playButton.dataset.f = 'sculpture';
        this.playButton.dataset.action = 'play';

        if(myHeader.classList.contains('w__header--half'))
        {
            this.playButton.classList.add('mod--header-half');
        }

        document.body.appendChild(this.playButton);
    }

    build_scene()
    {
        this.create_poles();
        this.create_ringInstances();
        this.create_circleInstances();
        this.set_initialMatrix();
    }

    create_poles()
    {
        const geometry = new THREE.BufferGeometry();
        const coordinates = [
            0, 0, 0,
            0, 0, this.poleLengh
        ];
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(coordinates,3));

        const material = new THREE.LineDashedMaterial({
            color: this.color,
            dashSize: .2,
            gapSize: .4,
            transparent: true,
            opacity: .5
        });

        for(let i=0; i<this.poleNumber; i++)
        {
            this.poles[i] = new THREE.Line(geometry, material);
            this.poles[i].scale.set(this.definitions[i][3], this.definitions[i][3], this.definitions[i][3]);
            this.poles[i].position.set(this.definitions[i][0], this.definitions[i][1], this.definitions[i][2]);
            this.poles[i].computeLineDistances();
            this.scene.add(this.poles[i]);
        }
    }

    create_ringInstances()
    {
        const ringGeometry = new THREE.TorusGeometry(this.radiusSize,this.tubeSize,4,64);
        const ringMaterial = new THREE.MeshBasicMaterial({
            color: this.color,
            transparent: true,
            opacity: .85
        });
        this.ring = new THREE.InstancedMesh(ringGeometry, ringMaterial, this.ringNumber);
        this.ring.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        this.ring.position.set(0,0,0);
        this.scene.add(this.ring);
    }

    create_circleInstances()
    {
        const circleGeometry = new THREE.CircleGeometry( this.radiusSize - this.tubeSize,64 );
        const circleMaterial = new THREE.MeshBasicMaterial({
            color: this.color,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: .1
        });
        this.circle = new THREE.InstancedMesh(circleGeometry, circleMaterial, this.ringNumber);
        this.circle.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        this.circle.position.set(0,0,0);
        this.scene.add(this.circle);
    }

    initialize_audio()
    {
        // initialize on first click, not before - iOS requires this
        if(this.listener === undefined)
        {
            this.listener = new THREE.AudioListener();
            this.camera.add(this.listener);
            this.audio = new THREE.Audio(this.listener);
            this.audioLoader = new THREE.AudioLoader();
            this.analyser = new THREE.AudioAnalyser(this.audio, this.fftSize);
        }
    }

    stop_audio()
    {
        if(this.audio && this.audio.isPlaying)
        {
            this.controls.classList.remove('mod--playing');
            this.audio.stop();
        }
    }

    start_audio()
    {
        // stop all other players
        Podcastplayer.stop_allPlayers();

        // set to playing
        this.controls.classList.add('mod--playing');

        // set button to stop
        const playButton = document.body.querySelector('#w--nav .w__button[data-action="toggle-play"]');
        if(playButton)
        {
            playButton.classList.add('mod--off');
        }

        // set or reset audio
        this.initialize_audio();
        this.perfSlow = 0;

        // apply audio config
        this.set_audioTrack();
        this.cfg = this.audios[this.activeAudioNo]["cfg"]["desktop"];
        this.update_params();

        this.audioLoader.load(
            this.audios[this.activeAudioNo]["src"],
            buffer => {
                this.audio.setBuffer(buffer);
                this.audio.setLoop(false);
                this.audio.setVolume(this.volume);
                this.audio.play();
                this.audio.source.onended = () => {
                    this.audio.isPlaying = false;
                    this.activeAudioNo++;
                    this.start_audio();
                };
            },
            progress => {
                if(this.debug)
                {
                    console.log('progress', progress);
                }
            },
            error => {
                console.log('error', error);
            });
    }

    set_cameraViewport()
    {
        this.camera.left = this.frustumSize / -2;
        this.camera.right = this.frustumSize / 2;
        this.camera.top = this.frustumSize / Display.ar / 2;
        this.camera.bottom = this.frustumSize / Display.ar / -2;
        this.camera.updateProjectionMatrix();
    }

    set_initialMatrix()
    {
        let k = 0,
            x, y, z,
            scale,
            scaleFactor = .025,
            moveLength = this.poleLengh / this.ringsPerPoleNumber / 2;

        for(let i=0; i<this.poleNumber; i++)
        {
            for(let j=0; j<this.ringsPerPoleNumber; j++)
            {
                x = this.definitions[i][0];
                y = this.definitions[i][1];
                z = this.definitions[i][2] + j * moveLength + (i%2 ? this.ringsPerPoleNumber * moveLength : 0);

                if(j < this.ringsPerPoleNumber / 2)
                {
                    scale = .01 + (scaleFactor * j);
                    this.scaleDirections[k] = 'down';
                }
                else
                {
                    this.scaleDirections[k] = 'up';
                    scale = (.01 + scaleFactor * (this.ringsPerPoleNumber - 1)) - (scaleFactor * j);
                }

                scale *= this.definitions[i][3];

                this.objectScale[k] = scale;
                this.dummy.scale.set(scale, scale, this.definitions[i][3]);
                this.dummy.position.set(x, y, z);
                this.dummy.updateMatrix();
                this.ring.setMatrixAt(k, this.dummy.matrix);
                this.circle.setMatrixAt(k, this.dummy.matrix);

                k++;
            }
        }

        this.ring.instanceMatrix.needsUpdate = true;
        this.circle.instanceMatrix.needsUpdate = true;
    }

    render()
    {
        const perfStart = performance.now();

        if(this.check_resized())
        {
            this.set_cameraViewport();
            this.renderer.setSize(Display.x, Display.y);
        }

        if(this.doCalculations === true)
        {
            if(this.audio && this.audio.isPlaying)
            {
                const step = 4;
                let myMatrixStart = {},
                    tempData;

                const data = this.analyser.getFrequencyData();
                for(let k = 0; k < this.ringNumber; k += step)
                {
                    myMatrixStart = this.get_transformations(data[k], k);

                    this.dummy.matrix.setPosition(myMatrixStart.position); // write new positon back
                    this.dummy.matrix.scale(myMatrixStart.scale);

                    this.ring.setMatrixAt(k, this.dummy.matrix);
                    this.circle.setMatrixAt(k, this.dummy.matrix);

                    for(let i = 1; i < step; i++)
                    {
                        if(k + i < this.ringNumber)
                        {
                            tempData = THREE.MathUtils.lerp(data[k], data[(k + i)], .5);

                            myMatrixStart = this.get_transformations(tempData, k + i);

                            this.dummy.matrix.setPosition(myMatrixStart.position); // write new positon back
                            this.dummy.matrix.scale(myMatrixStart.scale);

                            this.ring.setMatrixAt(k + i, this.dummy.matrix);
                            this.circle.setMatrixAt(k + i, this.dummy.matrix);
                        }
                    }
                }
            }
            else
            {
                this.waiting_ripples();
            }

            this.ring.instanceMatrix.needsUpdate = true;
            this.circle.instanceMatrix.needsUpdate = true;

            this.renderer.render(this.scene, this.camera);
        }

        requestAnimationFrame(() =>
        {
            // performance analysis
            if(this.perfTest === true)
            {
                if(performance.now() - perfStart > this.perfThreshold)
                {
                    this.perfSlow++;
                    if(this.perfSlow > 30)
                    {
                        this.perfTest = false;
                        this.doCalculations = false;
                        this.canvas.style.display = 'none';
                        this.controls.querySelector('.w__button[data-action="toggle-sculpture"]')
                            .classList.add('mod--off');
                    }
                }
            }

            this.render();
        });
    }

    process_click(el)
    {
        switch(el.dataset.action)
        {
            case 'play-id':
                for(let i=0; i<this.audios.length; i++)
                {
                    if(this.audios[i]["id"] === parseInt(el.dataset.audioId))
                    {
                        this.activeAudioNo = i;
                        this.controls.classList.add('active');
                        this.controlButton.classList.add('active');
                        this.controlButton.classList.add('mod--on');
                        this.start_audio();
                    }
                }
                break;

            case 'play':
                this.controls.classList.add('mod--playing');
                el.classList.add('mod--out')
                setTimeout(() => {
                    this.controlButton.classList.add('active');
                    this.controlButton.classList.add('mod--on');
                    this.controls.classList.add('active');
                },500);
                setTimeout(() => {
                    el.remove();
                }, 1000);
                this.start_audio();
                break;

            case 'stop':
                this.stop_audio();
                break;

            case 'next':
                this.activeAudioNo++;
                if(this.audio && this.audio.isPlaying)
                {
                    this.start_audio();
                }
                else
                {
                    this.set_audioTrack();
                }
                break;

            case 'prev':
                this.activeAudioNo--;
                if(this.audio && this.audio.isPlaying)
                {
                    this.start_audio();
                }
                else
                {
                    this.set_audioTrack();
                }
                break;

            case 'toggle-controls':
                el.classList.toggle('mod--on');
                this.controls.classList.toggle('active');
                break;

            case 'toggle-play':
                if(this.audio && this.audio.isPlaying)
                {
                    this.audio.pause();
                    this.controls.classList.remove('mod--playing');
                }
                else
                {
                    if(this.playButton)
                    {
                        this.playButton.remove();
                    }
                    this.start_audio();
                }
                break;

            case 'toggle-sculpture':
                el.classList.toggle('mod--off');
                if(el.classList.contains('mod--off'))
                {
                    this.canvas.style.display = 'none';
                    this.doCalculations = false;
                }
                else
                {
                    this.canvas.style.display = '';
                    this.doCalculations = true;
                }
                break;
        }
    }

    process_scroll(scrollY)
    {
        scrollY -= this.navHeight;
        if(scrollY < 0)
        {
            scrollY = 0;
        }

        if(scrollY > Display.y)
        {
            this.canvas.style.zIndex = '1';
        }
        else
        {
            this.canvas.style.zIndex = '';
        }

        const maxShiftY = 15,
              maxZoom = 5;
        let direction = this.lastScrollY > scrollY ? 'up' : 'down',
            newSceneX = 0,
            rotationChange = scrollY / 15,
            cameraChange = scrollY / 75,
            zoom = scrollY / 1500,
            blur = 0;

        this.lastScrollY = scrollY;

        if(this.zoomLevel > maxZoom)
        {
            zoom = this.zoomLevel - zoom;
        }
        else if(this.zoomLevel < maxZoom)
        {
            zoom = this.zoomLevel + zoom;
        }

        if(zoom > this.zoomLevel && zoom > maxZoom)
        {
            zoom = maxZoom;
        }
        else if(zoom < this.zoomLevel)
        {
            zoom = this.zoomLevel;
        }

        if(this.scrollSceneX === undefined)
        {
            this.scrollSceneX = THREE.MathUtils.radToDeg(this.scene.rotation.x);
        }

        if(this.scrollCameraY === undefined)
        {
            this.scrollCameraY = this.camera.position.y;
        }

        if(scrollY > 0)
        {
            if(this.scrollSceneX !== 0)
            {
                if(direction === 'down' && this.scrollSceneX > 0 ||
                    direction === 'up' && this.scrollSceneX > 0)
                {
                    rotationChange *= -1;
                }

                newSceneX = this.scrollSceneX + rotationChange;

                if(this.scrollSceneX > 0 && newSceneX < 0 ||
                   this.scrollSceneX < 0 && newSceneX > 0)
                {
                    newSceneX = 0;
                }

                this.scene.rotation.x = THREE.MathUtils.degToRad(newSceneX);
            }

            if(this.scrollCameraY > maxShiftY && this.scrollCameraY - cameraChange >= maxShiftY)
            {
                this.camera.position.y = this.scrollCameraY - cameraChange;
            }
            else if(this.scrollCameraY < maxShiftY && this.scrollCameraY + cameraChange <= maxShiftY)
            {
                this.camera.position.y = this.scrollCameraY + cameraChange;
            }

            blur = scrollY * 100 / Display.y / 8;
            if(blur > 35)
            {
                blur = 35;
            }
        }
        else
        {
            zoom = this.zoomLevel;
            this.scrollSceneX = undefined;
            this.scrollCameraY = undefined;

            // set default values for track
            if(this.audio && this.audio.isPlaying)
            {
                // use desktop config as fallback
                this.cfg = this.audios[this.activeAudioNo]['cfg'][Display.device]
                    ? this.audios[this.activeAudioNo]['cfg'][Display.device]
                    : this.audios[this.activeAudioNo]['cfg']['desktop'];
                this.update_params();
            }
            else
            {
//                this.reset_config();
                this.update_params();
            }
        }

        this.scene.scale.x = zoom;
        this.scene.scale.y = zoom;
        this.scene.scale.z = zoom;

        document.documentElement.style.setProperty('--blur',blur.toString() + 'px');
    }

    set_audioTrack()
    {
        if(this.activeAudioNo === this.audios.length)
        {
            this.activeAudioNo = 0;
        }
        else if(this.activeAudioNo < 0)
        {
            this.activeAudioNo = this.audios.length -1;
        }

        this.trackLabel.title = this.audios[this.activeAudioNo]['label'].replace('&amp;','&')
                                                                        .replace('&quot;','"')
                                                                        .replace('&#039;',"'")
                                                                        .replace('&gt;',">")
                                                                        .replace('&lt;',"<")
        this.trackLink.classList.remove('active');
        this.trackLink.href = this.audios[this.activeAudioNo]['path'] ? this.audios[this.activeAudioNo]['path'] : '';
        this.trackLink.innerHTML = this.audios[this.activeAudioNo]['label'];

        if(this.trackLink.getBoundingClientRect()['width'] > this.trackLabel.getBoundingClientRect()['width'])
        {
            this.trackLink.classList.add('active');
            let duration = Math.round((this.trackLink.getBoundingClientRect()['width']
                            - this.trackLabel.getBoundingClientRect()['width']) * 50);
            if(duration < 2000)
            {
                duration = 2000;
            }
            this.trackLink.style.animationDuration = duration.toString() + 'ms';
        }
    }

    update_params()
    {
        if(this.cfg['cam-x'] !== this.camera.position.x ||
            this.cfg['cam-y'] !== this.camera.position.y)
        {
            this.camera.position.x = this.cfg['cam-x'];
            this.camera.position.y = this.cfg['cam-y'];
            this.camera.updateProjectionMatrix();
        }

        this.zoomLevel = 1 + this.cfg['zoom'] / 100;
        this.scene.scale.x = this.zoomLevel;
        this.scene.scale.y = this.zoomLevel;
        this.scene.scale.z = this.zoomLevel;

        this.scene.rotation.set(
            THREE.MathUtils.degToRad(this.cfg['rot-x']),
            THREE.MathUtils.degToRad(this.cfg['rot-y']),
            THREE.MathUtils.degToRad(this.cfg['rot-z']));
    }

    // noinspection JSSuspiciousNameCombination
    waiting_ripples()
    {
        let k = 0,
            scaleVector = new THREE.Vector3(1,1,1),
            scaleBase,
            scaleFactor = .003,
            moveLength = this.poleLengh / this.ringsPerPoleNumber / 2;

        for(let i=0; i<this.poleNumber; i++)
        {
            scaleFactor += Math.sin(i) / 750;
            for(let j=0; j<this.ringsPerPoleNumber; j++)
            {
                this.ring.getMatrixAt(k, this.dummy.matrix);
                this.position.setFromMatrixPosition(this.dummy.matrix); // extract position from transformation matrix
                this.scale.setFromMatrixScale(this.dummy.matrix); // extract scale from transformation matrix

                scaleBase = 1 / this.scale.x;

                if(this.scaleDirections[k] === 'down')
                {
                    this.objectScale[k] -= scaleFactor;
                }
                else
                {
                    this.objectScale[k] += scaleFactor;
                }

                if(this.objectScale[k] > .385 && this.scaleDirections[k] === 'up')
                {
                    this.scaleDirections[k] = 'down';
                }
                else if(this.objectScale[k] < .001 && this.scaleDirections[k] === 'down')
                {
                    this.scaleDirections[k] = 'up';
                    this.position.z += this.ringsPerPoleNumber * moveLength;
                }

                if(Math.ceil(this.position.z - this.definitions[i][2]) > this.poleLengh)
                {
                    this.position.z = j * moveLength + this.definitions[i][2];
                    this.objectScale[k] = scaleFactor;// + scaleFactor / 2 * j;
                }

                scaleVector.x = scaleBase * this.objectScale[k]
                    + (i > this.poleNumber / 2
                        ? Math.sin((1 + k) * (i+1)) / 5
                        : Math.cos((1 + k) * (i+1)) / 6);
                // noinspection JSSuspiciousNameCombination
                scaleVector.y = scaleVector.x;
                scaleVector.z = 1;

                this.dummy.matrix.setPosition(this.position); // write new positon back
                this.dummy.matrix.scale(scaleVector);

                this.ring.setMatrixAt(k, this.dummy.matrix);
                this.circle.setMatrixAt(k, this.dummy.matrix);

                k++;
            }
        }
        this.counter++;
    }

    get_transformations(data, k)
    {
        const scale = new THREE.Vector3(),
            scaleVector = new THREE.Vector3(1,1,1),
            position =  new THREE.Vector3();

        this.ring.getMatrixAt(k, this.dummy.matrix);
        scale.setFromMatrixScale(this.dummy.matrix); // extract scale from transformation matrix
        position.setFromMatrixPosition(this.dummy.matrix); // extract scale from transformation matrix

        // position
        const tempY = data === 0 && k % this.ringsPerPoleNumber
            ? 1000
            : data / this.audioRatio * 1.1;

        if(position.z === 1000)
        {
            position.z = 0;
        }

        position.z = tempY === 1000 ? 1000 : THREE.MathUtils.lerp(position.z, tempY, .025);

        const scaleBasis = 1 / scale.x;

        if(data === 0)
        {
            scaleVector.x = .001;
        }
        else if(data < 50)
        {
            scaleVector.x = data / 500;
        }
        else if(data < 75)
        {
            scaleVector.x -=  data / 50;
        }
        else if(data < 125)
        {
            scaleVector.x -= data / 200;
        }
        else
        {
            scaleVector.x += Math.log(data) / 75;
        }

        scale.x = THREE.MathUtils.lerp(scale.x, scaleVector.x, .05) * scaleBasis;

        if(data > 150)
        {
            scale.x -= Math.abs(150 - data) / 1000;
        }

        // noinspection JSSuspiciousNameCombination
        scale.y = scale.x;

        return {
            scale: scale,
            position: position,
            originalScale: scaleVector.x
        };
    }

    reset_config()
    {
        this.cfg = {
            'rot-x': -80,
            'rot-y': 0,
            'rot-z': 0,
            'cam-x': 0,
            'cam-y': 0,
            'zoom': 0
        };
    }

    reset_matrix()
    {
        let reScale,
            scaleVector = new THREE.Vector3(1,1,1);
        for(let k=0; k<this.ringNumber; k++)
        {
            this.ring.getMatrixAt(k, this.dummy.matrix);
            this.scale.setFromMatrixScale(this.dummy.matrix); // extract scale from transformation matrix
            reScale = 1 / this.scale.x;
            scaleVector.x = reScale;
            scaleVector.y = reScale;
            this.dummy.matrix.scale(scaleVector);
            this.ring.setMatrixAt(k, this.dummy.matrix);
            this.circle.setMatrixAt(k, this.dummy.matrix);
        }
        this.ring.instanceMatrix.needsUpdate = true;
        this.circle.instanceMatrix.needsUpdate = true;
    }

    check_resized()
    {
        return this.renderer.domElement.width !== Display.x || this.renderer.domElement.height !== Display.y;
    }
}